import { Text, VStack } from "@chakra-ui/layout";
import { Trans, t } from "@lingui/macro";
import type { SortOrder as GraphqlSortOrder } from "@src/__generated__/graphql";
import type {
  ProjectRevenueOrderByColumnEnum,
  SortOrder as UrqlSortOrder,
} from "@src/__generated__/urql-graphql";
import { ACTIONS_COLUMN_ID, DataTable } from "@src/components/ui-kit";
import { currency, percent } from "@src/utils/formatters";
import { lowerCase } from "lodash";
import { observer } from "mobx-react-lite";
import { Fragment, useMemo } from "react";
import { ProfitAndRevenueAmount } from "../../../common/components";
import { createProjectColumns, withProjectHeaderTooltip } from "../../common";
import { ModalProjectReportDetail } from "../../common/DetailModal";
import type { ProjectReportColumn } from "../../common/types";
import type { ProjectProfitAndRevenueModel } from "../models";
import { useStore } from "../store";

type ProjectProfitAndRevenueRow = ProjectProfitAndRevenueModel &
  Record<string, unknown>;

export const Table = observer(function Table() {
  const store = useStore();
  const columns = useMemo<
    ProjectReportColumn<ProjectProfitAndRevenueRow>[]
  >(() => {
    const cols = createProjectColumns<ProjectProfitAndRevenueRow>({
      onDetailClick: store.reportStore.openDetailModal,
    });

    cols.push({
      id: "_ourWorkIncome",
      meta: { label: t`Work Invoiced`, align: "right" },
      header: withProjectHeaderTooltip(
        <VStack align="end">
          <Text fontWeight="inherit">
            <Trans>Work Invoiced</Trans>
          </Text>
          <Text fontWeight="inherit" color="grey.400">
            <Trans>Work Profit</Trans>
          </Text>
        </VStack>,
        <Trans>
          Invoiced amount of Work budget items compared to actual profit on
          work.
        </Trans>,
      ),
      cell: ({ row }) => (
        <VStack pr="5" align="end">
          <Text>
            {currency.formatByWorkspace(
              row.original.our_work_billings_for_date_range,
            )}
          </Text>
          <Text color="grey.400">
            {currency.formatByWorkspace(
              row.original.our_work_revenue_for_date_range,
            )}
          </Text>
        </VStack>
      ),
    });

    store.reportStore.expenseBudgetCategories.forEach((expCategory) => {
      cols.push({
        id: `billings_${expCategory.id}`,
        meta: {
          label: `${expCategory.name} ${t`Invoiced`}`,
          align: "right",
        },
        header: withProjectHeaderTooltip(
          <VStack align="end">
            <Text fontWeight="inherit">
              {expCategory.name}&nbsp;
              <Trans>Invoiced</Trans>
            </Text>
            <Text fontWeight="inherit" color="grey.400">
              {expCategory.name}&nbsp;
              <Trans>Profit</Trans>
            </Text>
          </VStack>,
          t`Invoiced amount of ${lowerCase(expCategory.name)} budget items compared to ${expCategory.name.toLowerCase()} profit on selected period.`,
        ),
        cell: ({ row }) => {
          const billings = row.original.expenses_billings_for_date_range.find(
            (category) =>
              category.expense_budget_category_id === expCategory.id,
          )?.billings;
          const profit = row.original.expenses_profit_for_date_range.find(
            (category) =>
              category.expense_budget_category_id === expCategory.id,
          )?.profit;

          return (
            <VStack pr="5" align="end">
              <Text>{currency.formatByWorkspace(billings ?? 0)}</Text>
              <Text color="grey.400">
                {typeof profit === "number"
                  ? currency.formatByWorkspace(profit)
                  : "-"}
              </Text>
            </VStack>
          );
        },
      });
    });

    cols.push(
      {
        id: "total_invoiced",
        meta: { label: t`Total Invoiced`, align: "right" },
        header: withProjectHeaderTooltip(
          <VStack align="end">
            <Text fontWeight="inherit">
              <Trans>Total Invoiced</Trans>
            </Text>
            <Text fontWeight="inherit" color="grey.400">
              <Trans>vs Total Profit</Trans>
            </Text>
          </VStack>,
          <Trans>
            Sum of the invoiced amount for Work, Media and Production vs It is
            summary of Work profit + Production Profit + Media Profit
          </Trans>,
        ),
        cell: ({ row }) => (
          <VStack pr="5" align="end">
            <Text>
              {currency.formatByWorkspace(row.original.total_invoiced)}
            </Text>
            <Text color="grey.400">
              {currency.formatByWorkspace(
                row.original.gross_profit_for_date_range,
              )}
            </Text>
          </VStack>
        ),
      },
      {
        id: "_ourGrossIncome",
        meta: { label: t`Gross Income`, align: "right" },
        header: withProjectHeaderTooltip(
          <Trans>Gross Income</Trans>,
          <Trans>
            Sum of Work Invoiced plus Production, Media and Royalties Profits.
          </Trans>,
        ),
        cell: ({ row }) => (
          <VStack align="end">
            <ProfitAndRevenueAmount
              amount={row.original.gross_income_for_date_range}
            />
            <Text>&nbsp;</Text>
          </VStack>
        ),
      },
      {
        id: "invoiced-share",
        meta: { label: t`Share Invoiced`, align: "right" },
        header: withProjectHeaderTooltip(
          <VStack align="end">
            <Text fontWeight="inherit">
              <Trans>Share Invoiced</Trans>
            </Text>
            <Text fontWeight="inherit" color="grey.400">
              <Trans>Share Profit</Trans>
            </Text>
          </VStack>,
          <Trans>
            Percentage of total invoicing for the selected period and applied
            filters.
          </Trans>,
        ),
        cell: ({ row }) => (
          <VStack align="right" pr="5">
            <Text>{percent.format(row.original.filtered_revenue_share)}</Text>
            <Text color="gray.400">
              {percent.format(row.original.total_revenue_share)}
            </Text>
          </VStack>
        ),
      },
      {
        id: ACTIONS_COLUMN_ID,
      },
    );

    return cols;
  }, [
    store.reportStore.expenseBudgetCategories,
    store.reportStore.openDetailModal,
  ]);

  const orderedBy = store.reportStore.orderBy.clauses[0];

  return (
    <Fragment>
      <DataTable.Body<
        ProjectProfitAndRevenueRow,
        ProjectRevenueOrderByColumnEnum
      >
        tableKey={store.key}
        data={store.projects as ProjectProfitAndRevenueRow[]}
        columns={columns}
        orderedBy={
          orderedBy
            ? (() => {
                const order = orderedBy.order as GraphqlSortOrder | undefined;

                if (!order) {
                  return undefined;
                }

                return {
                  column: orderedBy.column,
                  order,
                };
              })()
            : undefined
        }
        onOrderByChange={(clause) => {
          if (!clause.order) {
            store.reportStore.orderBy.setClauses([]);
            return;
          }

          const order = clause.order as UrqlSortOrder | undefined;

          if (!order) {
            store.reportStore.orderBy.setClauses([]);
            return;
          }

          store.reportStore.orderBy.setClauses([
            { column: clause.column, order },
          ]);
        }}
      />
      <ModalProjectReportDetail store={store.reportStore} />
    </Fragment>
  );
});
